// File: /js/shared/handlers/handleAjaxOnInput.js

import { buildContext } from '/js/shared/context/requestContext.js';
import { ajaxAction }   from '/js/shared/ajax/ajaxAction.js';

let debounceTimers = new WeakMap();
// Pending (debounced) editors, so a page unload can flush them (audit F2,
// 2026-09-13): the last keystrokes within the 500 ms window used to be lost
// on refresh / back. `keepalive` lets the request outlive the page.
const pending = new Set();
let unloadHookBound = false;

function bindUnloadFlush() {
  if (unloadHookBound) return;
  unloadHookBound = true;
  window.addEventListener('pagehide', () => {
    for (const el of Array.from(pending)) {
      const timer = debounceTimers.get(el);
      if (timer) clearTimeout(timer);
      debounceTimers.delete(el);
      pending.delete(el);
      dispatchEdit(el, { keepalive: true });
    }
  });
}

function dispatchEdit(el, fetchOptions = {}) {
  // `buildContext(el)` applies the scope-hash override if `el` is
  // inside an `[data-scope-hash]` ancestor (e.g. richtext editor
  // embedded inside an associated social-card). Without this, AJAX
  // would dispatch against the URL's hash and the server resolver
  // wouldn't have a registered handler for the embedded content.
  const data = {
    ...buildContext(el),
    ...el.dataset
  };
  // Extract the parent .block block-id if available
  const block = el.closest('.block');
  if (block?.dataset.blockId) {
    data.block_id = block.dataset.blockId;
  }

  // Attach value or innerHTML
  if (el.value !== undefined) {
    data.new_value = el.value;
  } else if (el.innerHTML !== undefined) {
    data.new_value = el.innerHTML;
  }
  ajaxAction(data, true, null, fetchOptions);
}

export function handleAjaxOnInput(event) {
  const el = event.target.closest('[data-ajax-oninput="true"]');
  if (!el || !el.dataset.name) return;

  // Opt-in min-length gate (e.g. the content search). A non-empty value
  // shorter than the threshold is ignored — it can only match the full
  // list server-side anyway. An EMPTY value still fires so clearing the
  // field resets to the full list. Inputs without the attribute are
  // unaffected.
  const minLen = parseInt(el.dataset.ajaxMinLength ?? '', 10);
  if (Number.isFinite(minLen) && minLen > 0 && typeof el.value === 'string') {
    const len = el.value.trim().length;
    if (len > 0 && len < minLen) {
      if (debounceTimers.has(el)) {
        clearTimeout(debounceTimers.get(el));
        debounceTimers.delete(el);
      }
      return;
    }
  }

  if (debounceTimers.has(el)) {
    clearTimeout(debounceTimers.get(el));
  }

  bindUnloadFlush();
  pending.add(el);

  const timer = setTimeout(() => {
    debounceTimers.delete(el);
    pending.delete(el);
    dispatchEdit(el);
  }, 500);

  debounceTimers.set(el, timer);
}
